/**
 * The automatic half of the brief/folder association; the manual path writes
 * the same link from the folder side, through the same service.
 *
 * The transition and the folder are two requests, in that order and never the
 * other one: the server only accepts a folder against a brief that is already
 * Ready. That ordering is also what lets a folder failure leave the transition
 * standing — by the time it can fail, the brief is Ready and saying so.
 *
 * `hasTransitioned` is what keeps a retry honest. Once the transition has
 * landed, pressing the button again creates only the folder; re-submitting
 * would log a second Ready against a brief that never left it.
 *
 * A brief that already delivers somewhere is offered no name field, which is
 * where "repeating the transition creates no duplicate" actually lives: the
 * dialog reads the association back from the server every time it opens, so
 * there is no local notion of "already done" to go stale.
 */
import { useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Spinner,
} from '@monksflow/monks-ui';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { BriefResponseDto } from '@inference.engine/workspace-api-client/web';

import { useSubmitBrief } from '@/entities/brief';
import { useCreateFolder } from '@/features/manage-project-folders';
import { notificationService } from '@/lib/services/notification.service';
import { parseProblemDetail } from '@/shared/utils/problem-details';

import {
  buildMarkBriefReadyFormSchema,
  type MarkBriefReadyFormData,
} from './mark-brief-ready.schema';

export interface MarkBriefReadyDialogProps {
  open: boolean;
  brief: BriefResponseDto;
  /** Platform project id — the id the `projects/:platformProjectId/*` routes are scoped to. */
  platformProjectId: string;
  onClose: () => void;
}

export function MarkBriefReadyDialog({
  open,
  brief,
  platformProjectId,
  onClose,
}: MarkBriefReadyDialogProps) {
  const { t } = useTranslation('projects');

  const linkedFolders = brief.approvalFolders;
  const hasLinkedFolder = linkedFolders.length > 0;

  const [hasTransitioned, setHasTransitioned] = useState(false);

  const formSchema = useMemo(() => buildMarkBriefReadyFormSchema(t), [t]);
  const form = useForm<MarkBriefReadyFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: brief.title },
    // Submit-time rather than `onTouched`: the dialog autofocuses this field,
    // so a blur-time check fires the moment a user who emptied it reaches for
    // the close button, and the message it renders grows the centred dialog
    // enough to slide that 16px button out from under the pointer — swallowing
    // the very click that asked to close. Pressing the CTA still surfaces an
    // empty name, which is the only point the user can act on it.
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  // Reopening starts a fresh attempt: the name follows the brief's current
  // title, and a transition landed under a previous opening is the server's
  // business now, not this dialog's.
  useEffect(() => {
    if (open) {
      form.reset({ name: brief.title });
      setHasTransitioned(false);
    }
  }, [open, brief.title, form]);

  const submitMutation = useSubmitBrief({ projectId: platformProjectId, briefId: brief.id });
  const createFolderMutation = useCreateFolder({ projectId: platformProjectId });
  const isPending = submitMutation.isPending || createFolderMutation.isPending;

  const transitionToReady = async (): Promise<boolean> => {
    if (hasTransitioned) return true;
    try {
      await submitMutation.mutateAsync({});
    } catch (error) {
      notificationService.showError(t('projectBriefDetail.failedToMarkAsReady'), {
        description: error instanceof Error ? error.message : undefined,
      });
      return false;
    }
    setHasTransitioned(true);
    notificationService.showSuccess(t('projectBriefDetail.briefMarkedAsReady'));
    return true;
  };

  const handleWithoutFolder = () => {
    void (async () => {
      if (await transitionToReady()) onClose();
    })();
  };

  const handleWithFolder = form.handleSubmit(async ({ name }) => {
    if (!(await transitionToReady())) return;

    try {
      const folder = await createFolderMutation.mutateAsync({
        data: { name: name.trim(), briefId: brief.id },
      });
      notificationService.showSuccess(
        t('projectBriefDetail.markBriefReadyDialog.folderCreated', { name: folder.name }),
      );
      onClose();
    } catch (error) {
      const { status, detail } = parseProblemDetail(
        error,
        t('projectBriefDetail.markBriefReadyDialog.failedToCreateFolder'),
      );
      // Sibling folder names are unique, so a collision is answered in the
      // field that caused it and the dialog stays up to be retried. The brief
      // is Ready either way — only the folder is still outstanding.
      if (status === 409) {
        form.setError('name', { type: 'server', message: detail });
        return;
      }
      notificationService.showError(detail);
    }
  });

  const handleClose = () => {
    if (isPending) return;
    form.clearErrors();
    onClose();
  };

  const blockWhilePending = (event: { preventDefault: () => void }) => {
    if (isPending) event.preventDefault();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next: boolean) => {
        if (!next) handleClose();
      }}
    >
      <DialogContent
        className="gap-0 p-4 max-w-120"
        showCloseButton={!isPending}
        onEscapeKeyDown={blockWhilePending}
        onPointerDownOutside={blockWhilePending}
        onInteractOutside={blockWhilePending}
        data-testid="mark-brief-ready-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('projectBriefDetail.markBriefReadyDialog.title')}</DialogTitle>
          <DialogDescription>
            {hasLinkedFolder
              ? t('projectBriefDetail.markBriefReadyDialog.alreadyDelivered', {
                  folders: linkedFolders.map((folder) => folder.name).join(', '),
                })
              : t('projectBriefDetail.markBriefReadyDialog.willCreateFolder')}
          </DialogDescription>
        </DialogHeader>

        {hasLinkedFolder ? (
          <DialogFooter className="pt-6">
            <Button
              variant="default"
              disabled={isPending}
              onClick={handleWithoutFolder}
              data-testid="mark-brief-ready-confirm"
            >
              {isPending && <Spinner />}
              {t('projectBriefDetail.markBriefReadyDialog.markAsReady')}
            </Button>
          </DialogFooter>
        ) : (
          <Form {...form}>
            <form
              onSubmit={(event) => {
                void handleWithFolder(event);
              }}
              className="flex flex-col"
            >
              <DialogBody className="flex flex-col gap-4 py-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('projectBriefDetail.markBriefReadyDialog.folderName')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          disabled={isPending}
                          data-testid="mark-brief-ready-folder-name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </DialogBody>
              {/* Stacked, and the primary spells out what the click does: the
                  point of the dialog is that nothing is created behind the
                  user's back, which a bare "Confirm" would undo. */}
              <DialogFooter className="flex-col gap-2 sm:flex-col sm:gap-2">
                <Button
                  type="submit"
                  variant="default"
                  className="w-full"
                  disabled={isPending}
                  data-testid="mark-brief-ready-confirm"
                >
                  {isPending && <Spinner />}
                  {hasTransitioned
                    ? t('projectBriefDetail.markBriefReadyDialog.createFolder')
                    : t('projectBriefDetail.markBriefReadyDialog.createFolderAndMarkAsReady')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  disabled={isPending}
                  onClick={hasTransitioned ? handleClose : handleWithoutFolder}
                  data-testid="mark-brief-ready-skip"
                >
                  {hasTransitioned
                    ? t('projectBriefDetail.markBriefReadyDialog.close')
                    : t('projectBriefDetail.markBriefReadyDialog.markAsReadyWithoutFolder')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
